import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import {
  DollarSign, Users, TrendingUp, ArrowRight, Star,
  CheckCircle, ChevronRight, Zap, Shield, Globe, Loader2
} from 'lucide-react'
import SEO from '@/components/layout/SEO'

// ─── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  navy: '#1a2740',
  navyMid: '#243452',
  sage: '#4a7c5e',
  sageDark: '#2d5a3d',
  sageMid: '#b8d9c4',
  sageLight: '#e8f2ec',
  cream: '#faf7f2',
  white: '#ffffff',
  gold: '#c9903a',
  goldLight: '#fdf6e9',
  slate: '#475569',
  muted: '#8a94a6',
  border: '#e2e8f0',
}

const heroGrad = `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`
const sageGrad = `linear-gradient(135deg, ${T.sage}, ${T.sageDark})`

interface Tier {
  id: string
  tier_key: string
  label: string
  commission_rate: number
  min_referrals: number
  sort_order: number
}

// Tier colors by sort_order index
const TIER_COLORS = [T.sage, T.gold, '#8b5cf6', '#3b82f6', '#ef4444']

interface TierCardProps {
  tier: Tier
  idx: number
  active?: boolean
  t: (key: string) => string
}

function TierCard({ tier, idx, active, t }: TierCardProps) {
  const color = TIER_COLORS[idx % TIER_COLORS.length]
  return (
    <motion.div
      whileHover={{ y: -4 }}
      style={{
        flex: 1, minWidth: 200,
        background: active ? heroGrad : T.white,
        borderRadius: 20, padding: '28px 24px',
        border: `2px solid ${active ? 'transparent' : T.border}`,
        boxShadow: active ? '0 16px 48px rgba(26,39,64,0.25)' : '0 2px 12px rgba(0,0,0,0.04)',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Status badge removed as this is a tiered progression system */}
      <div style={{
        fontSize: 48, fontWeight: 900, lineHeight: 1,
        color: active ? T.white : color, marginBottom: 8,
      }}>{tier.commission_rate}%</div>
      <div style={{
        fontSize: 16, fontWeight: 800,
        color: active ? 'rgba(255,255,255,0.9)' : T.navy,
        marginBottom: 6,
      }}>{tier.label}</div>
      <div style={{
        fontSize: 13, color: active ? 'rgba(255,255,255,0.6)' : T.muted,
      }}>
        {tier.min_referrals === 0
          ? (idx === 0 ? t('affiliates.tiers.starterTrigger') : t('affiliates.tiers.starterTrigger')) // Fallback to generic if needed
          : t('affiliates.tiers.growthTrigger').replace('10', tier.min_referrals.toString())
        }
      </div>
    </motion.div>
  )
}

export default function AffiliateLandingPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuth()
  const [searchParams] = useSearchParams()
  const [tiers, setTiers] = useState<Tier[]>([])
  const [discountPct, setDiscountPct] = useState<number>(5)
  const [tiersLoading, setTiersLoading] = useState(true)
  const [joining, setJoining] = useState(false)
  const [userProfile, setUserProfile] = useState<any>(null)

  // Fetch local user profile to check affiliate status if logged in
  useEffect(() => {
    if (user) {
      supabase.from('users' as any).select('is_affiliate, affiliate_status').eq('id', user.id).maybeSingle()
        .then(({ data }) => setUserProfile(data))
    }
  }, [user])

  // Store ref code from URL so it persists through signup flow
  useEffect(() => {
    const ref = searchParams.get('ref')
    if (ref) {
      localStorage.setItem('sage_affiliate_ref', ref.toUpperCase())
    }
  }, [searchParams])

  // Fetch dynamic tiers (public read — no auth needed)
  useEffect(() => {
    async function fetchTiers() {
      try {
        const [tiersRes, settingsRes] = await Promise.all([
          supabase.from('affiliate_commission_tiers').select('*').eq('is_active', true).order('sort_order', { ascending: true }),
          supabase.from('affiliate_settings').select('*').eq('key', 'customer_discount_pct').maybeSingle(),
        ])
        if (tiersRes.data && tiersRes.data.length > 0) setTiers(tiersRes.data as unknown as Tier[])
        if (settingsRes.data) setDiscountPct(Number(settingsRes.data.value))
      } catch {
        // fall through — defaults shown via empty state
      } finally {
        setTiersLoading(false)
      }
    }
    fetchTiers()
  }, [])

  const faqs = [
    { q: t('affiliates.faq.q1'), a: t('affiliates.faq.a1') },
    { q: t('affiliates.faq.q2'), a: t('affiliates.faq.a2') },
    { q: t('affiliates.faq.q3'), a: t('affiliates.faq.a3') },
    { q: t('affiliates.faq.q4'), a: t('affiliates.faq.a4') },
    { q: t('affiliates.faq.q5'), a: t('affiliates.faq.a5') },
    { q: t('affiliates.faq.q6'), a: t('affiliates.faq.a6') },
  ]

  const steps = [
    { n: '01', title: t('affiliates.howItWorks.step1.title'), desc: t('affiliates.howItWorks.step1.desc') },
    { n: '02', title: t('affiliates.howItWorks.step2.title'), desc: t('affiliates.howItWorks.step2.desc') },
    { n: '03', title: t('affiliates.howItWorks.step3.title'), desc: t('affiliates.howItWorks.step3.desc') },
    { n: '04', title: t('affiliates.howItWorks.step4.title'), desc: t('affiliates.howItWorks.step4.desc') },
  ]

  const handleJoinInstant = async () => {
    if (!user) {
      navigate('/auth/login?next=/affiliates/apply')
      return
    }
    setJoining(true)
    try {
      // Call edge function — with auto-approval logic for authenticated users
      const { data, error } = await supabase.functions.invoke('affiliate-apply', {
        body: {
          full_name: user.user_metadata?.full_name || '',
          email: user.email || '',
          promo_channel: 'member_join',
        }
      })
      if (error) throw error
      navigate('/affiliates/dashboard')
    } catch (err) {
      console.error('Join Error:', err)
      // Fallback to manual form if any issue
      navigate('/affiliates/apply')
    } finally {
      setJoining(false)
    }
  }

  // Pick the "most popular" tier (middle one, or marked growth tier)
  const activeTierIdx = tiers.length > 1 ? Math.floor(tiers.length / 2) : 0
  // Top rate for hero stats
  const topRate = tiers.length > 0 ? Math.max(...tiers.map(t => t.commission_rate)) : '—'

  return (
    <div style={{ background: T.cream, fontFamily: 'system-ui, sans-serif', minHeight: '100vh' }}>
      <SEO
        title={t('affiliates.hero.headline')}
        description={t('affiliates.hero.subheadline')}
        url="https://stillwatersacademy.com/affiliates"
      />

      {/* ── HERO ── */}
      <div style={{ background: heroGrad }}>
        {/* Sub-header row */}
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
            ← Back to Still Waters
          </Link>
          <span style={{ fontSize: 11, fontWeight: 700, color: T.sageMid, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Affiliate Partner Program
          </span>
        </div>

        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 72px' }}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto' }}
          >
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(74,124,94,0.25)', border: '1px solid rgba(74,124,94,0.4)',
              borderRadius: 24, padding: '6px 18px', marginBottom: 28,
            }}>
              <DollarSign size={14} color={T.sage} />
              <span style={{ fontSize: 12, fontWeight: 800, color: T.sage, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                {t('affiliates.hero.badge')}
              </span>
            </div>

            <h1 style={{ fontSize: 54, fontWeight: 900, color: T.white, lineHeight: 1.1, margin: '0 0 20px', letterSpacing: '-0.02em' }}>
              {t('affiliates.hero.headline')}
              <span style={{ color: T.sage }}> {t('affiliates.hero.headlineAccent')}</span>
            </h1>

            <p style={{ fontSize: 20, color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, margin: '0 0 40px' }}>
              {t('affiliates.hero.subheadline')}
            </p>

            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
              {authLoading || tiersLoading ? (
                <div style={{ padding: '16px 36px' }}><Loader2 className="animate-spin" size={24} color={T.white} /></div>
              ) : !user ? (
                <>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => navigate('/affiliates/apply')}
                    style={{
                      padding: '16px 36px', borderRadius: 14,
                      background: sageGrad, color: T.white,
                      border: 'none', fontWeight: 800, fontSize: 17,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                      boxShadow: '0 8px 28px rgba(74,124,94,0.4)',
                    }}
                  >
                    {t('affiliates.hero.cta')} <ArrowRight size={18} />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    onClick={() => navigate('/auth/login?next=/affiliates/dashboard')}
                    style={{
                      padding: '16px 28px', borderRadius: 14,
                      background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      fontWeight: 700, fontSize: 16, cursor: 'pointer',
                    }}
                  >
                    {t('affiliates.hero.alreadyApproved')}
                  </motion.button>
                </>
              ) : userProfile?.is_affiliate ? (
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  onClick={() => navigate('/affiliates/dashboard')}
                  style={{
                    padding: '16px 36px', borderRadius: 14,
                    background: sageGrad, color: T.white,
                    border: 'none', fontWeight: 800, fontSize: 17,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                    boxShadow: '0 8px 28px rgba(74,124,94,0.4)',
                  }}
                >
                  {t('affiliates.hero.goToDashboard', 'Go to Dashboard')} <ArrowRight size={18} />
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  disabled={joining}
                  onClick={handleJoinInstant}
                  style={{
                    padding: '16px 36px', borderRadius: 14,
                    background: sageGrad, color: T.white,
                    border: 'none', fontWeight: 800, fontSize: 17,
                    cursor: joining ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 8,
                    boxShadow: '0 8px 28px rgba(74,124,94,0.4)',
                  }}
                >
                  {joining ? <Loader2 className="animate-spin" size={18} /> : <Zap size={18} />}
                  {t('affiliates.hero.joinNow', 'Join Affiliates')}
                </motion.button>
              )}
            </div>
          </motion.div>

          {/* Stats bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{
              display: 'flex', gap: 0, marginTop: 72, flexWrap: 'wrap',
              background: 'rgba(255,255,255,0.05)', borderRadius: 20,
              border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden',
            }}
          >
            {[
              { icon: <DollarSign size={20} />, value: tiersLoading ? '…' : `Up to ${topRate}%`, sub: 'Lifetime commission' },
              { icon: <Users size={20} />, value: t('affiliates.stats.lifetime'), sub: t('affiliates.stats.lifetimeSub') },
              { icon: <TrendingUp size={20} />, value: `${discountPct}%`, sub: t('affiliates.stats.discountSub') },
              { icon: <Globe size={20} />, value: t('affiliates.stats.platforms'), sub: t('affiliates.stats.platformsSub') },
            ].map((stat, i) => (
              <div key={i} style={{
                flex: 1, minWidth: 180, padding: '28px 24px', textAlign: 'center',
                borderRight: i < 3 ? '1px solid rgba(255,255,255,0.08)' : 'none',
              }}>
                <div style={{ color: T.sage, display: 'flex', justifyContent: 'center', marginBottom: 10 }}>{stat.icon}</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: T.white, marginBottom: 4 }}>{stat.value}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>{stat.sub}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Wave */}
        <svg viewBox="0 0 1440 48" style={{ display: 'block', width: '100%', height: 48 }} preserveAspectRatio="none">
          <path d="M0,48 C360,0 1080,48 1440,20 L1440,48 Z" fill={T.cream} />
        </svg>
      </div>

      {/* ── COMMISSION TIERS ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '72px 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontSize: 38, fontWeight: 900, color: T.navy, margin: '0 0 14px' }}>
            {t('affiliates.tiers.title')}
          </h2>
          <p style={{ fontSize: 17, color: T.muted, maxWidth: 540, margin: '0 auto' }}>
            {t('affiliates.tiers.subtitle')}
          </p>
        </div>

        {tiersLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Loader2 size={32} color={T.sage} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
            {tiers.map((tier, idx) => (
              <TierCard key={tier.id} tier={tier} idx={idx} active={idx === activeTierIdx} t={t} />
            ))}
          </div>
        )}

        <div style={{
          marginTop: 32, background: T.sageLight, borderRadius: 16,
          padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <CheckCircle size={20} color={T.sage} style={{ flexShrink: 0 }} />
          <p style={{ margin: 0, fontSize: 15, color: T.sageDark, fontWeight: 600 }}>
            {t('affiliates.tiers.subtitle')}
          </p>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <div style={{ background: T.white, padding: '72px 0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <h2 style={{ fontSize: 38, fontWeight: 900, color: T.navy, margin: '0 0 14px' }}>
              {t('affiliates.howItWorks.title')}
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 28 }}>
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                style={{ padding: '28px 24px', borderRadius: 18, border: `1px solid ${T.border}` }}
              >
                <div style={{
                  fontSize: 36, fontWeight: 900, color: T.sageLight,
                  fontVariantNumeric: 'tabular-nums', marginBottom: 16,
                }}>{step.n}</div>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: T.navy, margin: '0 0 10px' }}>{step.title}</h3>
                <p style={{ fontSize: 14, color: T.slate, lineHeight: 1.6, margin: 0 }}>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CUSTOMER DISCOUNT ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '72px 24px' }}>
        <div style={{
          background: heroGrad, borderRadius: 24, padding: '48px 40px',
          display: 'flex', alignItems: 'center', gap: 40, flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: T.sage, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>
              {t('affiliates.discount.badge')}
            </div>
            <h2 style={{ fontSize: 32, fontWeight: 900, color: T.white, margin: '0 0 16px', lineHeight: 1.2 }}>
              {t('affiliates.discount.title')}
            </h2>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, margin: 0 }}>
              {t('affiliates.discount.body')}
            </p>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.06)', borderRadius: 18,
            padding: '32px 40px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)',
            flexShrink: 0,
          }}>
            <div style={{ fontSize: 64, fontWeight: 900, color: T.white, lineHeight: 1 }}>
              {tiersLoading ? '…' : `${discountPct}%`}
            </div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginTop: 8 }}>
              {t('affiliates.discount.offFirstPayment')}
            </div>
          </div>
        </div>
      </div>

      {/* ── WHO IS IT FOR ── */}
      <div style={{ background: T.white, padding: '72px 0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 38, fontWeight: 900, color: T.navy, margin: '0 0 14px' }}>
              {t('affiliates.whoFor.title')}
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
            {[
              { icon: '📝', label: t('affiliates.whoFor.bloggers') },
              { icon: '🎥', label: t('affiliates.whoFor.youtubers') },
              { icon: '🎙', label: t('affiliates.whoFor.podcasters') },
              { icon: '🏫', label: t('affiliates.whoFor.educators') },
              { icon: '👨‍👩‍👧', label: t('affiliates.whoFor.parentGroups') },
              { icon: '⛪', label: t('affiliates.whoFor.churches') },
            ].map((item, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -3 }}
                style={{
                  background: T.cream, borderRadius: 16, padding: '24px 20px',
                  textAlign: 'center', border: `1px solid ${T.border}`,
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 12 }}>{item.icon}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.navy }}>{item.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ── TRUST SIGNALS ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '72px 24px' }}>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
          {[
            { icon: <Shield size={22} />, title: t('affiliates.trust.reliable'), body: t('affiliates.trust.reliableBody') },
            { icon: <Zap size={22} />, title: t('affiliates.trust.realtime'), body: t('affiliates.trust.realtimeBody') },
            { icon: <Star size={22} />, title: t('affiliates.trust.support'), body: t('affiliates.trust.supportBody') },
          ].map((item, i) => (
            <div key={i} style={{
              flex: 1, minWidth: 240, background: T.white, borderRadius: 18,
              padding: '28px 24px', border: `1px solid ${T.border}`,
            }}>
              <div style={{ color: T.sage, marginBottom: 14 }}>{item.icon}</div>
              <h4 style={{ fontSize: 17, fontWeight: 800, color: T.navy, margin: '0 0 8px' }}>{item.title}</h4>
              <p style={{ fontSize: 14, color: T.slate, lineHeight: 1.6, margin: 0 }}>{item.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── FAQ ── */}
      <div style={{ background: T.white, padding: '72px 0' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 38, fontWeight: 900, color: T.navy, margin: 0 }}>
              {t('affiliates.faq.title')}
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {faqs.map((faq, i) => (
              <FAQItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </div>

      {/* ── FINAL CTA ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '72px 24px 96px' }}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{
            background: sageGrad, borderRadius: 28, padding: '64px 40px',
            textAlign: 'center',
          }}
        >
          <h2 style={{ fontSize: 40, fontWeight: 900, color: T.white, margin: '0 0 16px' }}>
            {t('affiliates.finalCta.title')}
          </h2>
          <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.8)', margin: '0 0 36px' }}>
            {t('affiliates.finalCta.body')}
          </p>
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate('/affiliates/apply')}
            style={{
              padding: '18px 44px', borderRadius: 14,
              background: T.white, color: T.sageDark,
              border: 'none', fontWeight: 900, fontSize: 18,
              cursor: 'pointer', boxShadow: '0 8px 28px rgba(0,0,0,0.15)',
            }}
          >
            {t('affiliates.finalCta.cta')} <ArrowRight size={18} style={{ display: 'inline', marginLeft: 6 }} />
          </motion.button>
          <p style={{ marginTop: 20, fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
            {t('affiliates.finalCta.note')}
          </p>

          <div style={{ marginTop: 64, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 40 }}>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', marginBottom: 20 }}>
              {t('affiliates.finalCta.alreadyMember', 'Already a member? Enable your affiliate dashboard instantly.')}
            </p>
            <motion.button
              whileHover={{ scale: 1.03 }}
              disabled={joining}
              onClick={handleJoinInstant}
              style={{
                padding: '14px 28px', borderRadius: 12,
                background: joining ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
                color: T.white,
                border: '1px solid rgba(255,255,255,0.2)',
                fontWeight: 700, fontSize: 15, cursor: joining ? 'not-allowed' : 'pointer',
              }}
            >
              {joining ? <Loader2 className="animate-spin" size={18} style={{ display: 'inline', marginRight: 8 }} /> : <Users size={18} style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />}
              {userProfile?.is_affiliate ? t('affiliates.finalCta.goToDashboard', 'Go to Dashboard') : t('affiliates.finalCta.becomeAffiliate', 'Become an Affiliate')}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = React.useState(false)
  return (
    <div style={{ border: `1px solid #e2e8f0`, borderRadius: 12, overflow: 'hidden', marginBottom: 8 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', padding: '18px 20px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 700, color: '#1a2740' }}>{q}</span>
        <ChevronRight size={18} color="#8a94a6" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{ padding: '0 20px 18px', fontSize: 14, color: '#475569', lineHeight: 1.7 }}>{a}</div>
      )}
    </div>
  )
}
